import React from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import theme from 'shared/styles/theme';

export interface RenderHtmlOfferSectionViewProps {
  titleText: string | undefined;
  content?: string | undefined;
  showDivider: boolean;
  linkText: string;
  id: string;
  handlePdfClick: () => void;
}

const Divider = () => <View style={styles.dividerStyle} />;

const { width } = Dimensions.get('window');

const RenderHtmlOfferSectionView = ({
  content,
  titleText,
  showDivider,
  linkText,
  handlePdfClick,
}: RenderHtmlOfferSectionViewProps) => {
  const titlesWithoutSecondaryColor = ['How To Use', 'About', 'Benefits'];
  const titlesWithBodyXsFont = ['Terms', 'Disclosure'];

  const contentTextColor = titlesWithoutSecondaryColor.includes(titleText || '')
    ? theme.color.text.primary
    : theme.color.text.secondary;

  const bodyTextFont = titlesWithBodyXsFont.includes(titleText || '')
    ? theme.typography.body.sm
    : theme.typography.body.base;

  const dynamicHtmlStyles = {
    ...htmlStyles,
    body: {
      ...(theme.typography.body.sm as MixedStyleDeclaration),
      color: contentTextColor,
    },
    li: {
      // without casting to MixedStyleDeclaration we get a type error in tagStyles props below
      ...(theme.typography.body.base as MixedStyleDeclaration),
      marginBottom: theme.spacing.s,
    },
    ol: {
      paddingLeft: theme.spacing.s,
    },
    p: {
      ...(bodyTextFont as MixedStyleDeclaration),
      color: contentTextColor,
    },
  };

  const renderers = {
    a: () => {
      return (
        <HyperlinkXsText onPress={handlePdfClick}>{linkText}</HyperlinkXsText>
      );
    },
  };

  return (
    <>
      <View style={styles.viewMargin}>
        {titleText ? (
          <TitleSmText numberOfLines={1}>{titleText}</TitleSmText>
        ) : null}
        <View style={styles.sectionStyle} testID="content-html">
          <HTMLRenderer
            htmlAsString={content as string}
            contentWidth={width}
            tagsStyles={dynamicHtmlStyles}
            renderers={renderers}
            renderersProps={renderProps}
          />
        </View>
      </View>
      {showDivider ? <Divider /> : null}
    </>
  );
};

const styles = StyleSheet.create({
  dividerStyle: {
    backgroundColor: theme.color.background.surface,
    padding: theme.spacing.xs,
  },
  sectionStyle: {
    marginTop: theme.spacing.s,
  },
  viewMargin: {
    margin: theme.spacing.m,
  },
});

type MixedStyleDeclaration = {
  color?: string;
  fontSize?: number;
  paddingLeft?: number;
  fontFamily?: string;
};

const renderProps = {
  ol: {
    markerTextStyle: {
      ...theme.typography.body.baseBold,
      alignSelf: 'flex-start',
      textAlign: 'left',
    },
    markerBoxStyle: {
      maxWidth: theme.spacing.l,
      minWidth: theme.spacing.l,
    },
  },
};

const htmlStyles: Readonly<Record<string, MixedStyleDeclaration>> =
  StyleSheet.create({
    // Disabled ESLint for unused styles in this file, as they're passed to the HTMLRenderer component.
    // eslint-disable-next-line react-native/no-unused-styles
    a: {
      ...(theme.typography.hyperlink.xs as MixedStyleDeclaration),
      color: theme.color.interactive.primary,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    body: {
      color: theme.color.text.secondary,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    li: {
      paddingLeft: theme.spacing.s,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    ol: {
      paddingLeft: theme.spacing.s,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    p: {
      ...(theme.typography.body.base as MixedStyleDeclaration),
      color: theme.color.text.primary,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    ul: {
      ...(theme.typography.body.base as MixedStyleDeclaration),
      paddingLeft: theme.spacing.s,
    },
  });

export default React.memo(RenderHtmlOfferSectionView);
